import { createContext, useContext, useEffect, useState } from "react";
import { API_URL } from "@/utils/api";

// Railway places the current public CMS payload into the initial HTML for /.
// Starting both header and homepage from the same value prevents the old
// JavaScript defaults from flashing before the first network response.
const BOOTSTRAP_ID = "elido-nexus-home-bootstrap";
const NexusHomeContentContext = createContext(null);

function readNexusBootstrap() {
  if (typeof document === "undefined") return null;
  const node = document.getElementById(BOOTSTRAP_ID);
  if (!node) return null;
  try {
    const data = JSON.parse(node.textContent || "null");
    if (data?.success === true && data.content?.hero && data.content?.site) {
      return data.content;
    }
  } catch (e) {
    console.warn("Nexus home bootstrap JSON invalid", e);
  }
  return null;
}

export function NexusHomeContentProvider({ children }) {
  const [state, setState] = useState(() => {
    const content = readNexusBootstrap();
    return content
      ? { status: "ready", content }
      : { status: "loading", content: null };
  });

  useEffect(() => {
    // A fresh bootstrap was already included in this HTML response.
    // SPA navigations and legacy/failed shells fetch the same CMS data once.
    if (state.status === "ready") return undefined;
    let active = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/website/nexus-home`, {
          signal: controller.signal,
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`CMS status ${res.status}`);
        const data = await res.json();
        if (data?.success !== true || !data.content?.hero || !data.content?.site) {
          throw new Error("CMS returned incomplete Nexus home content");
        }
        if (active) setState({ status: "ready", content: data.content });
      } catch (error) {
        if (active) {
          console.error("Failed to load nexus home content", error);
          setState({ status: "error", content: null });
        }
      } finally {
        clearTimeout(timer);
      }
    })();

    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
    // The initial state is intentional: only one fetch per NexusLayout mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NexusHomeContentContext.Provider value={state}>
      {children}
    </NexusHomeContentContext.Provider>
  );
}

export function useNexusHomeContent() {
  const context = useContext(NexusHomeContentContext);
  if (!context) {
    throw new Error("Nexus home content must be used within its provider");
  }
  return context;
}
